import { useQuery } from '@apollo/client';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PETS_QUERY } from '@/modules/customers-pets/pets.gql';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';

import { ClinicalHistoryModal } from './ClinicalHistoryModal';
import { ClinicalVisitStatusBadge } from './ClinicalVisitStatusBadge';
import { MY_PENDING_CLINICAL_VISITS_QUERY } from './clinical.gql';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

/**
 * Clinical records hub. A patient search (any patient, not scoped to the current doctor)
 * opens that patient's lifetime history + visits; the doctor's own pending visits are
 * listed below for quick access. Both open the read-only ClinicalHistoryModal.
 */
export function ClinicalPendingPage(): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [historyPetId, setHistoryPetId] = useState<string | null>(null);

  useEffect(() => {
    const h = window.setTimeout(() => setDebounced(search.trim()), 250);
    return () => window.clearTimeout(h);
  }, [search]);

  const canSearch = debounced.length >= 2;
  const { data: petsData, loading: petsLoading } = useQuery(PETS_QUERY, {
    variables: { offset: 0, limit: 15, search: debounced, status_filter: 'ACTIVE', include_deceased: true },
    skip: !canSearch,
    fetchPolicy: 'cache-and-network',
  });
  const pets: any[] = petsData?.pets?.items ?? [];

  const { data: pendingData, loading: pendingLoading } = useQuery(MY_PENDING_CLINICAL_VISITS_QUERY, {
    variables: { offset: 0, limit: 50 },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
  const pending: any[] = pendingData?.myPendingClinicalVisits?.items ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4">
      <div>
        <h1 className="font-display text-xl font-bold tracking-tight">{t('clinical.records_title')}</h1>
        <p className="text-muted-foreground text-sm">{t('clinical.records_subtitle')}</p>
      </div>

      {/* Patient search */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('clinical.search_patient_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="relative">
            <Search className="text-muted-foreground absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden />
            <Input
              className="pl-8"
              placeholder={t('clinical.search_patient_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {!canSearch ? (
            <p className="text-muted-foreground text-xs">{t('clinical.search_min_hint')}</p>
          ) : petsLoading && pets.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
          ) : pets.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('clinical.search_no_results')}</p>
          ) : (
            <ul className="divide-border divide-y">
              {pets.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setHistoryPetId(p.id)}
                    className="hover:bg-muted flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm"
                  >
                    <span>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground">
                        {p.species ? ` · ${p.species}` : ''}
                        {p.breed ? ` · ${p.breed}` : ''}
                        {p.record?.code ? ` · ${p.record.code}` : ''}
                      </span>
                      {p.is_deceased ? (
                        <span className="text-destructive"> · {t('clinical.deceased')}</span>
                      ) : null}
                    </span>
                    <span className="text-muted-foreground text-xs">{t('clinical.view_record')}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Doctor's pending visits */}
      <div>
        <h2 className="font-display mb-2 text-sm font-bold tracking-tight">{t('clinical.pending_title')}</h2>
        {pendingLoading && pending.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        ) : pending.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <TotemMark className="text-muted-foreground/40 h-10 w-auto" aria-hidden />
            <p className="text-muted-foreground text-sm">{t('clinical.pending_empty')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((v) => (
              <Card
                key={v.id}
                className="hover:border-primary/50 cursor-pointer"
                onClick={() => setHistoryPetId(v.pet_id)}
              >
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 py-3">
                  <CardTitle className="text-sm">{v.folio_number}</CardTitle>
                  <ClinicalVisitStatusBadge status={v.status} />
                </CardHeader>
                <CardContent className="text-muted-foreground py-0 pb-3 text-xs">
                  {v.chief_complaint ? <p className="line-clamp-2">{v.chief_complaint}</p> : null}
                  <p>{new Date(v.created_at).toLocaleString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {historyPetId ? (
        <ClinicalHistoryModal pet_id={historyPetId} onClose={() => setHistoryPetId(null)} />
      ) : null}
    </div>
  );
}
